import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';

import ProjectSecretNameField from '@/components/projects/Project/ProjectSecrets/ProjectSecretForm/ProjectSecretNameField';
import ProjectSecretValueField from '@/components/projects/Project/ProjectSecrets/ProjectSecretForm/ProjectSecretValueField';
import { putSecret } from '@/actions/projectSecrets';

export default function ProjectSecretForm({
  open,
  onClose,
  mode,
  secret,
  project,
  onSaved,
}) {
  const dispatch = useDispatch();
  const [secret_name, setSecretName] = useState('');
  const [secret_value, setSecretValue] = useState('');
  const [name_error, setNameError] = useState(false);
  const [value_error, setValueError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [value_field_key, setValueFieldKey] = useState(0);

  useEffect(() => {
    if (!open) {
      return;
    }
    setValueFieldKey((k) => k + 1);
    setNameError(false);
    setValueError(false);
    if (mode === 'edit' && secret) {
      setSecretName(secret.name || '');
      setSecretValue('');
    } else {
      setSecretName('');
      setSecretValue('');
    }
  }, [open, mode, secret]);

  const handleClose = useCallback(() => {
    if (!submitting) {
      onClose();
    }
  }, [onClose, submitting]);

  const handleSubmit = useCallback(async () => {
    const name_trimmed = secret_name.trim();
    const value_trimmed = secret_value;
    let has_error = false;
    if (!name_trimmed) {
      setNameError(true);
      has_error = true;
    } else {
      setNameError(false);
    }
    if (!value_trimmed) {
      setValueError(true);
      has_error = true;
    } else {
      setValueError(false);
    }
    if (has_error) {
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(
        putSecret(project.id, project.id, name_trimmed, value_trimmed),
      );
      if (onSaved) {
        onSaved();
      }
      onClose();
    } catch {
      // ErrorSnackBar via setErrorMessage in thunk
    } finally {
      setSubmitting(false);
    }
  }, [
    dispatch,
    onClose,
    onSaved,
    project.id,
    secret_name,
    secret_value,
  ]);

  const title = mode === 'edit' ? 'Edit project secret' : 'Add project secret';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          {title}
          <IconButton
            aria-label='close'
            onClick={handleClose}
            disabled={submitting}
            size='small'
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <ProjectSecretNameField
          mode={mode}
          value={secret_name}
          onChange={(e) => setSecretName(e.target.value)}
          error={name_error}
          helper_text={name_error ? 'Name is required' : ''}
        />
        <ProjectSecretValueField
          key={value_field_key}
          mode={mode}
          value={secret_value}
          onChange={(e) => setSecretValue(e.target.value)}
          error={value_error}
          helper_text={value_error ? 'Value is required' : ''}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={submitting}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
